import axios, { AxiosRequestConfig, AxiosResponse } from "axios";

import {
    LocalStorageKey,
    LocalStorageKeyCustomToken,
    rollbar,
} from "utils";
import { API_BASE_URL, APP_VERSION } from "config_environment";
import { auth } from "./auth";
import { communities } from "./communities";
import { communitiesJoined } from "./communitiesJoined";
import { community } from "./community";
import { docs } from "./docs";
import { events } from "./events";
import { groups } from "./groups";
import { marketplaceOffer } from "./marketplaceOffer";
import { marketplaceOffers } from "./marketplaceOffers";
import { member } from "./member";
import { members } from "./members";
import { messages } from "./messages";
import { offers } from "./offers";
import { posts } from "./posts";
import { profile } from "./profile";
import { userNotifications } from "./userNotifications";
import { videos } from "./videos";
import { resources } from "./resources";
import { viewCounts } from "./viewCounts";
import { payments } from "./payments";
import { subscription } from "./subscription";

const defaultOptions = {
    baseURL: API_BASE_URL,
    headers: {
        "Content-Type": "application/json",
        "X-UHUBS-PLATFORM": "web",
        "X-UHUBS-PLATFORM-VERSION": APP_VERSION,
    }
    // withCredentials: true
};

// Performance tracking thresholds (in milliseconds)
const PERFORMANCE_THRESHOLDS = {
    warning: 5000,
    error: 10000,
};

// Create instance
let instance = axios.create(defaultOptions);
let interceptor: number | null = null;

// Add request interceptor to track start time
instance.interceptors.request.use((config) => {
    (config as any).metadata = { startTime: performance.now() };
    return config;
});

// Add response interceptor for performance tracking
instance.interceptors.response.use(
    (response: AxiosResponse) => {
        const config = response.config as any;
        if (config.metadata?.startTime) {
            const elapsed = Math.round(performance.now() - config.metadata.startTime);
            const endpoint = `${config.method?.toUpperCase()} ${config.url}`;

            const customData = {
                elapsed_ms: elapsed,
                endpoint,
                method: config.method,
                url: config.url,
                status: response.status,
            };
            console.log(elapsed, customData)

            if (elapsed > PERFORMANCE_THRESHOLDS.error) {
                rollbar.error(`Slow API: ${endpoint} (${elapsed}ms)`, customData);
            } else if (elapsed > PERFORMANCE_THRESHOLDS.warning) {
                console.warn(elapsed, customData)
                rollbar.warning(`Slow API: ${endpoint} (${elapsed}ms)`, customData);
            }
        }
        return response;
    },
    (error) => {
        const config = error.config as any;
        if (config?.metadata?.startTime) {
            const elapsed = Math.round(performance.now() - config.metadata.startTime);
            const endpoint = `${config.method?.toUpperCase()} ${config.url}`;

            rollbar.error(`API Error: ${endpoint} (${elapsed}ms)`, {
                elapsed_ms: elapsed,
                endpoint,
                method: config.method,
                url: config.url,
                status: error.response?.status,
                error: error.message,
            });
        }
        return Promise.reject(error);
    }
);

export const updateToken = (token: string) => {
    if (token) localStorage.setItem(LocalStorageKey, token);
    if (interceptor) instance.interceptors.request.eject(interceptor);
    interceptor = instance.interceptors.request.use(function (
        config: AxiosRequestConfig<any>,
    ) {
        if (!config) {
            return;
        } else {
            // At this, typescript erroneously thinks that config might be undefined
            // @ts-ignore
            config.headers["Authorization"] = token ?? "";
            return config;
        }
    });
};

export const updateHeaders = (headers: { [key: string]: string }[]) => {

    if (interceptor) instance.interceptors.request.eject(interceptor);
    interceptor = instance.interceptors.request.use(function (
        config: AxiosRequestConfig<any>,
    ) {
        if (!config) {
            return;
        } else {
            headers.forEach((header) => {
                // @ts-ignore
                config.headers[header.name.toString()] = header.value || '';
            })
            return config;
        }
    });
}

export const clearToken = () => {
    localStorage.removeItem(LocalStorageKeyCustomToken);
    localStorage.removeItem(LocalStorageKey);
    instance = axios.create(defaultOptions);
};

// if (responseInterceptor)
//   instance.interceptors.response.eject(responseInterceptor);

// Add a response interceptor
axios.interceptors.response.use(
    function (response) {
        // Do something with the response
        return response;
    },
    function (error) {
        // try {
        //   const response = JSON.parse(
        //     String.fromCharCode.apply(
        //       null,
        //       // @ts-ignore
        //       new Uint8Array(error.response.data)
        //     )
        //   );
        //   if (response && response.errors === "Unauthorized access!") {
        //     history.push("/auth/sign-out");
        //   }
        // } catch (e) {}
        return Promise.reject(error);
    },
);

export const axiosInstance = () => instance;

export const api = {
    auth,
    communities,
    community,
    groups,
    profile,
    posts,
    marketplaceOffers,
    marketplaceOffer,
    events,
    communitiesJoined,
    member,
    members,
    messages,
    docs,
    videos,
    resources,
    offers,
    userNotifications,
    viewCounts,
    payments,
    subscription,
};
export { notify } from "./notify";
